/**
 * `copyMembers` (§5.3, D37/D115/D118) — P15 surface 4's first screen. Ported from
 * `prototype/screens/copy-members.js`; P14 (`packages/functions/members/copy.ts`) owns every
 * semantic, this screen owns every pixel (P15 §6).
 *
 * **Three steps, not the prototype's three-branches-in-one-render.** `pick` chooses the other
 * managed gate (D115 offers the same one-time operation from either Access-list entry point);
 * `review` shows who is about to be copied, by name, and commits the write; `result` is the
 * per-row outcome report §13 "A manager copies gate A's member list to gate B (D37)" requires.
 *
 * **`direction` decides which end the anchor gate is** (`web/src/router/routes.ts`): `"from"` means
 * the anchor is the *target* and the manager is choosing a source (the "Copy from another gate…"
 * entry point); `"to"` means the anchor is the *source* and the manager is choosing a target ("Copy
 * to another gate…"). The eligible-gate list is a UX narrowing, not the control — P14 refuses a
 * non-manager of either gate server-side regardless of what this screen offers.
 *
 * **The review step now carries a real per-row preview, by name — `GET /gates/{id}/members` on
 * the source gate, the same read `PeopleScreen`'s roster already uses.** Every role always
 * copies; there is no managers-toggle to narrow it, so the preview is the whole source roster
 * minus the `installer` row P14 never copies (D44). Approving "copy members" now means approving
 * a named list, not a direction and an on/off switch — logged as a deviation in
 * `implementation-notes.md` → P15.
 */
import { useState } from 'react'
import type { GateId } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import {
  AppBar,
  Bidi,
  MemberPhone,
  OutcomeReport,
  PrimaryButton,
  SecondaryButton,
  ShareSheet,
} from '../../components'
import type { OutcomeRowView, OutcomeTone } from '../../components'
import type { Key } from '../../i18n/catalogs'
import { useTranslation } from '../../i18n/I18nProvider'
import { useNavigation } from '../../router'
import { getRoster, inviteMember, type RosterRow } from '../shared/api'
import { copyMembers, type CopyMembersResult, type CopyRow } from './api'

type RosterState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly rows: readonly RosterRow[] }
  | { readonly phase: 'failed' }

type Step =
  | { readonly kind: 'pick' }
  | { readonly kind: 'review'; readonly otherGateId: GateId; readonly roster: RosterState }
  | { readonly kind: 'copying'; readonly otherGateId: GateId; readonly roster: readonly RosterRow[] }
  | {
      readonly kind: 'result'
      readonly otherGateId: GateId
      readonly result: CopyMembersResult
      readonly roster: readonly RosterRow[]
    }

type ShareState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly text: string; readonly url: string }
  | { readonly phase: 'failed' }

export function CopyMembersScreen({
  gateId,
  direction,
}: {
  readonly gateId: GateId
  readonly direction: 'from' | 'to'
}): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t, locale } = useTranslation('copyMembers')

  const anchor = deviceCache.gates.find((g) => g.gateId === gateId)
  const anchorName = anchor?.alias ?? anchor?.defaultName ?? ''

  const [step, setStep] = useState<Step>({ kind: 'pick' })
  /**
   * `too-large` is D335's `422 copy-too-large`, and it has its own sentence because `copyFailed`
   * says *try again* — and a retry of a copy over the bound is refused identically, forever.
   */
  const [copyError, setCopyError] = useState<'none' | 'failed' | 'too-large'>('none')
  /**
   * The source `GateId` a `too-large` refusal was measured against. The Copy button stays
   * disabled while `copyError === 'too-large'` for THIS source — a same-source retry would be
   * refused identically and would only inflate `CopyRefusedTooLarge` (D335) — and `pickGate`
   * clears `copyError` the moment the resolved source changes, which is what re-enables it.
   */
  const [tooLargeSource, setTooLargeSource] = useState<GateId | undefined>(undefined)
  const [shareState, setShareState] = useState<ShareState>({ phase: 'idle' })

  const candidates = deviceCache.gates.filter((g) => g.viewerRole === 'manager' && g.gateId !== gateId)

  function nameOf(id: string): string {
    const cached = deviceCache.gates.find((g) => g.gateId === id)
    return cached?.alias ?? cached?.defaultName ?? ''
  }

  function done(): void {
    navigate.resetTo({ id: 'people', gateId })
  }

  async function pickGate(otherGateId: GateId): Promise<void> {
    const sourceId = direction === 'from' ? otherGateId : gateId
    if (sourceId !== tooLargeSource) setCopyError('none')
    setStep({ kind: 'review', otherGateId, roster: { phase: 'loading' } })
    try {
      const response = await getRoster(session.request, sourceId)
      if (!response.ok) {
        setStep({ kind: 'review', otherGateId, roster: { phase: 'failed' } })
        return
      }
      setStep({ kind: 'review', otherGateId, roster: { phase: 'ready', rows: response.data.members } })
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setStep({ kind: 'review', otherGateId, roster: { phase: 'failed' } })
    }
  }

  async function runCopy(otherGateId: GateId, roster: readonly RosterRow[]): Promise<void> {
    setStep({ kind: 'copying', otherGateId, roster })
    setCopyError('none')
    const targetGateId = direction === 'from' ? gateId : otherGateId
    const sourceGateId = direction === 'from' ? otherGateId : gateId
    try {
      const response = await copyMembers(session.request, targetGateId, sourceGateId)
      if (!response.ok) {
        if (response.code === 'copy-too-large') {
          setCopyError('too-large')
          setTooLargeSource(sourceGateId)
        } else {
          setCopyError('failed')
        }
        setStep({ kind: 'review', otherGateId, roster: { phase: 'ready', rows: roster } })
        return
      }
      setStep({ kind: 'result', otherGateId, result: response.data, roster })
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setCopyError('failed')
      setStep({ kind: 'review', otherGateId, roster: { phase: 'ready', rows: roster } })
    }
  }

  async function fetchShare(result: CopyMembersResult, targetGateId: string): Promise<void> {
    // One of the newly copied *new* users' numbers, so the target gate's own invite endpoint
    // returns a share payload — see `./api.ts`'s doc comment for why this reuses `inviteMember()`
    // instead of a share field on the copy response, which P14 deliberately does not carry.
    const candidate = newPendingInviteOf(result)
    if (candidate === undefined) return
    setShareState({ phase: 'loading' })
    try {
      const response = await inviteMember(
        session.request,
        targetGateId,
        candidate.phone,
        locale,
        candidate.role,
      )
      if (!response.ok) {
        setShareState({ phase: 'failed' })
        return
      }
      setShareState({ phase: 'ready', text: response.data.share.text, url: response.data.share.url })
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setShareState({ phase: 'failed' })
    }
  }

  if (step.kind === 'pick') {
    return (
      <>
        <AppBar title={t('title')} onBack={() => navigate.back()} />
        <div className="section-title">
          {direction === 'from'
            ? t('pickPromptFrom', { name: anchorName })
            : t('pickPromptTo', { name: anchorName })}
        </div>
        {candidates.length === 0 ? (
          <div className="row">
            <div className="l">{t('pickEmpty')}</div>
          </div>
        ) : (
          candidates.map((gate) => (
            <button
              className="row"
              data-interactive="true"
              type="button"
              key={gate.gateId}
              onClick={() => void pickGate(gate.gateId as GateId)}
            >
              <div className="l">{gate.alias ?? gate.defaultName}</div>
            </button>
          ))
        )}
      </>
    )
  }

  const sourceId = direction === 'from' ? step.otherGateId : gateId
  const targetId = direction === 'from' ? gateId : step.otherGateId

  if (step.kind === 'review' || step.kind === 'copying') {
    const busy = step.kind === 'copying'
    const roster: RosterState = busy ? { phase: 'ready', rows: step.roster } : step.roster
    const previewRows = roster.phase === 'ready' ? roster.rows.filter((row) => !row.installer) : []
    return (
      <>
        <AppBar title={t('reviewTitle')} onBack={() => setStep({ kind: 'pick' })} />
        <div className="section-title">
          {t('fromTo', { source: nameOf(sourceId), target: nameOf(targetId) })}
        </div>
        {roster.phase === 'loading' ? <p className="hint">{t('previewLoading')}</p> : null}
        {roster.phase === 'failed' ? (
          <>
            <p className="hint error">{t('previewFailed')}</p>
            <div className="form-row">
              <SecondaryButton onClick={() => void pickGate(step.otherGateId)}>
                {t('previewRetry')}
              </SecondaryButton>
            </div>
          </>
        ) : null}
        {roster.phase === 'ready' && previewRows.length === 0 ? (
          <div className="row">
            <div className="l">{t('previewEmpty')}</div>
          </div>
        ) : null}
        {previewRows.map((row) => (
          <div className="row" key={row.userId ?? row.phone}>
            <div className="l">
              <Bidi>{row.displayName}</Bidi>
              {/* D178. The outcome step below already showed a phone under a name, by hand
                  (`outcomeRowView`'s `detail`); this is the same pair on the step where the
                  manager is still deciding, which is where telling two people apart matters. */}
              <MemberPhone
                {...(row.phone === undefined ? {} : { phone: row.phone })}
                displayName={row.displayName}
              />
            </div>
            <div className="r">{row.role === 'manager' ? t('roleManager') : t('roleResident')}</div>
          </div>
        ))}
        {copyError === 'none' ? null : (
          <p className="hint error">{t(copyError === 'too-large' ? 'copyTooLarge' : 'copyFailed')}</p>
        )}
        <div className="form-row">
          <button
            className="btn-primary"
            type="button"
            disabled={
              busy ||
              roster.phase !== 'ready' ||
              previewRows.length === 0 ||
              (copyError === 'too-large' && sourceId === tooLargeSource)
            }
            onClick={() => void runCopy(step.otherGateId, roster.phase === 'ready' ? roster.rows : [])}
          >
            {busy ? t('copying') : t('copyButton')}
          </button>
        </div>
      </>
    )
  }

  // step.kind === 'result'
  const { result, roster } = step
  const rows: readonly OutcomeRowView[] = result.rows.map((row, index) =>
    outcomeRowView(row, index, roster, t),
  )
  // The share step is offered only when there is something new to share — a copy that skipped
  // every row as a duplicate has no pending invite to fetch a share payload for, and a button
  // that silently did nothing on tap would look broken rather than merely absent.
  const canShare = newPendingInviteOf(result) !== undefined

  return (
    <>
      <AppBar title={t('title')} onBack={done} />
      <div className="section-title">
        {t('fromTo', { source: nameOf(sourceId), target: nameOf(targetId) })}
      </div>
      <OutcomeReport rows={rows} />
      {!canShare ? null : shareState.phase === 'idle' ? (
        <div className="form-row">
          <SecondaryButton onClick={() => void fetchShare(result, targetId)}>
            {t('shareTitle')}
          </SecondaryButton>
        </div>
      ) : shareState.phase === 'loading' ? (
        <p className="hint">{t('copying')}</p>
      ) : shareState.phase === 'ready' ? (
        <>
          <div className="section-title">{t('shareTitle')}</div>
          <ShareSheet
            heading={t('shareTitle')}
            message={shareState.text}
            onSelect={(channel) => {
              if (channel === 'sms') window.open(`sms:?body=${encodeURIComponent(shareState.text)}`, '_blank')
              else if (channel === 'whatsapp')
                window.open(`https://wa.me/?text=${encodeURIComponent(shareState.text)}`, '_blank')
              else if (navigator.share !== undefined) void navigator.share({ text: shareState.text })
            }}
          />
        </>
      ) : (
        <p className="hint error">{t('copyFailed')}</p>
      )}
      <div className="form-row">
        <PrimaryButton onClick={done}>{t('done')}</PrimaryButton>
      </div>
    </>
  )
}

/**
 * The one newly copied row that still needs the target gate's own share dialog — a `pending`
 * `invite`-kind row that was actually `copied` (not skipped). See `fetchShare()`'s doc comment
 * for why this reuses `inviteMember()` rather than a `share` field on the copy response.
 */
function newPendingInviteOf(result: CopyMembersResult): (CopyRow & { readonly phone: string }) | undefined {
  return result.rows.find(
    (row): row is CopyRow & { readonly phone: string } =>
      row.kind === 'invite' &&
      row.outcome === 'copied' &&
      row.status === 'pending' &&
      row.phone !== undefined,
  )
}

/**
 * The source roster fetched for the pre-copy preview, reused here so the result step names the
 * same people rather than falling back to a phone number or a bare role label. `kind: 'member'`
 * rows match by `userId`, `kind: 'invite'` rows by `phone` — the two identifiers `CopyRow` itself
 * carries. A row that cannot be matched (the roster changed between preview and copy) falls back
 * to the phone or role label exactly as before this screen fetched any roster at all.
 */
function nameFor(row: CopyRow, roster: readonly RosterRow[]): string | undefined {
  if (row.kind === 'member' && row.userId !== undefined) {
    return roster.find((candidate) => candidate.userId === row.userId)?.displayName
  }
  if (row.kind === 'invite' && row.phone !== undefined) {
    return roster.find((candidate) => candidate.phone === row.phone)?.displayName
  }
  return undefined
}

function outcomeRowView(
  row: CopyRow,
  index: number,
  roster: readonly RosterRow[],
  t: (key: Key<'copyMembers'>, vars?: Readonly<Record<string, string | number>>) => string,
): OutcomeRowView {
  const name = nameFor(row, roster)
  const label = name ?? row.phone ?? (row.role === 'manager' ? t('roleManager') : t('roleResident'))
  const detail = name !== undefined && row.phone !== undefined && row.phone !== name ? row.phone : undefined
  const outcomeLabel =
    row.outcome === 'copied'
      ? t('outcomeCopied')
      : row.outcome === 'skipped-duplicate'
        ? t('outcomeSkippedDuplicate')
        : row.outcome === 'skipped-expired'
          ? t('outcomeSkippedExpired')
          : t('outcomeFailed')
  const tone: OutcomeTone =
    row.outcome === 'copied' ? 'positive' : row.outcome === 'failed' ? 'negative' : 'neutral'
  return {
    key: `${row.kind}-${row.userId ?? row.phone ?? index}`,
    label,
    ...(detail === undefined ? {} : { detail }),
    outcomeLabel,
    tone,
  }
}
